import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { StatusBadge } from "@/components/status/status-badge";
import { ProviderLogo } from "@/components/providers/destination-provider-logo";
import { api } from "@/lib/api";
import { openGoogleCalendarConsent } from "./google-calendar-connect-prompt";
import { openMicrosoftTeamsConsent } from "./microsoft-teams-connect-prompt";
import { openZoomConsent } from "./zoom-connect-prompt";
import {
  AVAILABLE_SOURCES,
  activeConnection,
  useIntegrationsOutlet,
} from "./integrations-shared";

/**
 * Bookmarklet (task: Eventbrite "Fill the form for me"). Drag to the bookmarks
 * bar, then click it while on Eventbrite's "Request a new key" page. It fills
 * the form fields by matching each field's label text — cross-origin policy
 * prevents filling the form directly, so the bookmarklet runs in the
 * Eventbrite page. It never touches the terms-of-use checkbox.
 */
const EVENTBRITE_FORM_FILL_BOOKMARKLET = `javascript:void(function(){var F=[[/application url/i,"https://app.attendeesync.com"],[/redirect/i,"https://app.attendeesync.com/oauth/eventbrite/callback"],[/application name/i,"AttendeeSync"],[/description/i,"Syncs event registrants into my marketing stack"]];var done=0;document.querySelectorAll("input[type=text],input:not([type]),textarea").forEach(function(el){var lbl=(el.getAttribute("aria-label")||"")+"|"+(el.getAttribute("placeholder")||"")+"|"+(el.id&&document.querySelector("label[for='"+el.id+"']")?document.querySelector("label[for='"+el.id+"']").innerText:"")+"|"+(el.closest("label")?el.closest("label").innerText:"");F.forEach(function(p){if(p[0].test(lbl)&&!el.value){el.focus();el.value=p[1];el.dispatchEvent(new Event("input",{bubbles:true}));el.dispatchEvent(new Event("change",{bubbles:true}));done++}})});alert(done?("AttendeeSync filled "+done+" field(s). Please review them, tick the terms box yourself, then Create Key."):("AttendeeSync could not match the form fields automatically. Please fill them in manually using the step-by-step instructions."))})();`;

export function IntegrationsSourcesTab() {
  const {
    orgId,
    canManage,
    organizationName,
    sourceConnections,
    setMessage,
    refresh,
  } = useIntegrationsOutlet();

  const [sourceConnectBusy, setSourceConnectBusy] = useState<string | null>(
    null,
  );
  const [heysummitConnecting, setHeysummitConnecting] = useState(false);
  const [heysummitToken, setHeysummitToken] = useState("");
  const [heysummitReveal, setHeysummitReveal] = useState(false);
  const [heysummitError, setHeysummitError] = useState<string | null>(null);
  const [heysummitBusy, setHeysummitBusy] = useState(false);
  const [fluentFormsConnecting, setFluentFormsConnecting] = useState(false);
  const [fluentFormsSiteUrl, setFluentFormsSiteUrl] = useState("");
  const [fluentFormsUsername, setFluentFormsUsername] = useState("");
  const [fluentFormsPassword, setFluentFormsPassword] = useState("");
  const [fluentFormsReveal, setFluentFormsReveal] = useState(false);
  const [fluentFormsError, setFluentFormsError] = useState<string | null>(null);
  const [fluentFormsBusy, setFluentFormsBusy] = useState(false);
  /** null = closed; "choose" = pick OAuth vs API key; "api_key" = paste key form */
  const [mailchimpConnecting, setMailchimpConnecting] = useState<
    "choose" | "api_key" | null
  >(null);
  const [mailchimpApiKey, setMailchimpApiKey] = useState("");
  const [mailchimpReveal, setMailchimpReveal] = useState(false);
  const [mailchimpError, setMailchimpError] = useState<string | null>(null);
  const [mailchimpBusy, setMailchimpBusy] = useState(false);
  const [eventbriteConnecting, setEventbriteConnecting] = useState<
    "choose" | "private_token" | null
  >(null);
  const [eventbriteToken, setEventbriteToken] = useState("");
  const [eventbriteReveal, setEventbriteReveal] = useState(false);
  const [eventbriteError, setEventbriteError] = useState<string | null>(null);
  const [eventbriteBusy, setEventbriteBusy] = useState(false);
  /** null = unknown (assume available); false = OAuth not configured on this deployment */
  const [mailchimpOAuth, setMailchimpOAuth] = useState<boolean | null>(null);
  const [eventbriteOAuth, setEventbriteOAuth] = useState<boolean | null>(null);
  const [mailchimpHelpOpen, setMailchimpHelpOpen] = useState(false);
  const [eventbriteHelpOpen, setEventbriteHelpOpen] = useState(false);
  const [sourceDisconnectBusy, setSourceDisconnectBusy] = useState<
    string | null
  >(null);
  const sourceConnectPanelRef = useRef<HTMLDivElement>(null);
  const credentialSourceConnecting =
    heysummitConnecting ||
    fluentFormsConnecting ||
    Boolean(mailchimpConnecting) ||
    Boolean(eventbriteConnecting);

  useEffect(() => {
    if (!credentialSourceConnecting) return;
    sourceConnectPanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [credentialSourceConnecting]);

  // Hide the OAuth button when OAuth is not configured on this deployment
  // (the API-key / private-token path is the working one there).
  useEffect(() => {
    if (!mailchimpConnecting) return;
    let cancelled = false;
    void api<{ oauthEnabled?: boolean }>(
      `/api/accounts/${orgId}/sources/mailchimp`,
    )
      .then((res) => {
        if (!cancelled) setMailchimpOAuth(res.oauthEnabled !== false);
      })
      .catch(() => {
        if (!cancelled) setMailchimpOAuth(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mailchimpConnecting, orgId]);

  useEffect(() => {
    if (!eventbriteConnecting) return;
    let cancelled = false;
    void api<{ oauthEnabled?: boolean }>(
      `/api/accounts/${orgId}/sources/eventbrite`,
    )
      .then((res) => {
        if (!cancelled) setEventbriteOAuth(res.oauthEnabled !== false);
      })
      .catch(() => {
        if (!cancelled) setEventbriteOAuth(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventbriteConnecting, orgId]);

  async function connectSource(provider: string, label: string) {
    if (!canManage || sourceConnectBusy) return;
    setMessage(null);
    if (provider === "google_meet") {
      openGoogleCalendarConsent("google_meet");
      return;
    }
    if (provider === "zoom") {
      openZoomConsent();
      return;
    }
    if (provider === "microsoft_teams") {
      openMicrosoftTeamsConsent();
      return;
    }
    if (provider === "heysummit") {
      setHeysummitConnecting(true);
      setHeysummitToken("");
      setHeysummitReveal(false);
      setHeysummitError(null);
      return;
    }
    if (provider === "fluent_forms") {
      setFluentFormsConnecting(true);
      setFluentFormsSiteUrl("");
      setFluentFormsUsername("");
      setFluentFormsPassword("");
      setFluentFormsReveal(false);
      setFluentFormsError(null);
      return;
    }
    if (provider === "mailchimp") {
      setMailchimpConnecting("choose");
      setMailchimpApiKey("");
      setMailchimpReveal(false);
      setMailchimpError(null);
      return;
    }
    if (provider === "eventbrite") {
      setEventbriteConnecting("choose");
      setEventbriteToken("");
      setEventbriteReveal(false);
      setEventbriteError(null);
      return;
    }
    setSourceConnectBusy(provider);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/${provider}/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organizationName ?? label,
            returnPath: "/integrations",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMessage(
        e instanceof Error ? e.message : `Could not start ${label} connection`,
      );
      setSourceConnectBusy(null);
    }
  }

  async function saveHeySummitToken() {
    const token = heysummitToken.trim();
    if (!token) {
      setHeysummitError("API token is required.");
      return;
    }
    setHeysummitBusy(true);
    setHeysummitError(null);
    try {
      await api(`/api/accounts/${orgId}/sources/heysummit/connect`, {
        method: "POST",
        body: JSON.stringify({ apiToken: token }),
      });
      setHeysummitConnecting(false);
      setHeysummitToken("");
      setMessage("HeySummit is connected.");
      await refresh();
    } catch (e) {
      setHeysummitError(
        e instanceof Error ? e.message : "Could not connect HeySummit",
      );
    } finally {
      setHeysummitBusy(false);
    }
  }

  async function disconnectSource(
    provider: string,
    label: string,
    connectionId: string,
  ) {
    if (!canManage || sourceDisconnectBusy) return;
    setSourceDisconnectBusy(connectionId);
    setMessage(null);
    try {
      await api(
        `/api/accounts/${orgId}/sources/${provider}/connections/${connectionId}/disconnect`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
      );
      setMessage(`${label} disconnected.`);
      await refresh();
    } catch (e) {
      setMessage(
        e instanceof Error ? e.message : `Could not disconnect ${label}`,
      );
    } finally {
      setSourceDisconnectBusy(null);
    }
  }

  async function saveFluentFormsCredentials() {
    const siteUrl = fluentFormsSiteUrl.trim();
    const username = fluentFormsUsername.trim();
    const applicationPassword = fluentFormsPassword.trim();
    if (!siteUrl || !username || !applicationPassword) {
      setFluentFormsError(
        "Site URL, username, and Application Password are required.",
      );
      return;
    }
    setFluentFormsBusy(true);
    setFluentFormsError(null);
    try {
      await api(`/api/accounts/${orgId}/sources/fluent_forms/connect`, {
        method: "POST",
        body: JSON.stringify({ siteUrl, username, applicationPassword }),
      });
      setFluentFormsConnecting(false);
      setFluentFormsSiteUrl("");
      setFluentFormsUsername("");
      setFluentFormsPassword("");
      setMessage(
        "Fluent Forms is connected. Configure a Pro webhook feed using the URL and secret shown below.",
      );
      await refresh();
    } catch (e) {
      setFluentFormsError(
        e instanceof Error ? e.message : "Could not connect Fluent Forms",
      );
    } finally {
      setFluentFormsBusy(false);
    }
  }

  async function startMailchimpOAuth() {
    if (!canManage || mailchimpBusy) return;
    setMailchimpBusy(true);
    setMailchimpError(null);
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/mailchimp/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organizationName ?? "Mailchimp",
            returnPath: "/integrations",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setMailchimpError(
        e instanceof Error ? e.message : "Could not start Mailchimp OAuth",
      );
      setMailchimpBusy(false);
    }
  }

  async function saveMailchimpApiKey() {
    const key = mailchimpApiKey.trim();
    if (!key) {
      setMailchimpError("API key is required.");
      return;
    }
    setMailchimpBusy(true);
    setMailchimpError(null);
    try {
      await api(`/api/accounts/${orgId}/sources/mailchimp/connect`, {
        method: "POST",
        body: JSON.stringify({ apiKey: key }),
      });
      setMailchimpConnecting(null);
      setMailchimpApiKey("");
      setMessage("Mailchimp is connected.");
      await refresh();
    } catch (e) {
      setMailchimpError(
        e instanceof Error ? e.message : "Could not connect Mailchimp",
      );
    } finally {
      setMailchimpBusy(false);
    }
  }

  async function startEventbriteOAuth() {
    if (!canManage || eventbriteBusy) return;
    setEventbriteBusy(true);
    setEventbriteError(null);
    setMessage(null);
    try {
      const result = await api<{ connectUrl: string }>(
        `/api/accounts/${orgId}/sources/eventbrite/connect-link`,
        {
          method: "POST",
          body: JSON.stringify({
            hostLabel: organizationName ?? "Eventbrite",
            returnPath: "/integrations",
          }),
        },
      );
      window.location.href = result.connectUrl;
    } catch (e) {
      setEventbriteError(
        e instanceof Error ? e.message : "Could not start Eventbrite OAuth",
      );
      setEventbriteBusy(false);
    }
  }

  async function saveEventbritePrivateToken() {
    const token = eventbriteToken.trim();
    if (!token) {
      setEventbriteError("Private token is required.");
      return;
    }
    setEventbriteBusy(true);
    setEventbriteError(null);
    try {
      await api(`/api/accounts/${orgId}/sources/eventbrite/connect`, {
        method: "POST",
        body: JSON.stringify({ privateToken: token }),
      });
      setEventbriteConnecting(null);
      setEventbriteToken("");
      setMessage("Eventbrite is connected.");
      await refresh();
    } catch (e) {
      setEventbriteError(
        e instanceof Error ? e.message : "Could not connect Eventbrite",
      );
    } finally {
      setEventbriteBusy(false);
    }
  }

  return (
    <Panel>
      <PanelHeader
        title="Sources"
        description="Event platforms that provide registrants"
      />
      <div className="flex flex-col gap-3 p-5">
        {AVAILABLE_SOURCES.map((source) => {
          const active = activeConnection(sourceConnections, source.provider);
          const connected = Boolean(active && active.status === "active");
          const detail = active
            ? connected
              ? `${active.displayName || active.email} · ${active.email}`
              : active.status
            : null;
          const busy = sourceConnectBusy === source.provider;
          const disconnectBusyId = active?.id ?? null;

          if (source.provider === "heysummit" && heysummitConnecting) {
            return (
              <div
                key={source.provider}
                ref={sourceConnectPanelRef}
                className="space-y-4 rounded-rs border border-rs-border bg-rs-bg p-4"
              >
                <div className="flex items-start gap-3">
                  <ProviderLogo provider="heysummit" label="HeySummit" />
                  <div>
                    <h3 className="text-base font-semibold text-rs-ink">
                      Connect HeySummit
                    </h3>
                    <p className="mt-1 text-sm text-rs-ink-muted">
                      Paste the API token from HeySummit Setup → API, MCP &amp;
                      Webhooks. AttendeeSync stores it encrypted and registers
                      webhooks automatically.
                    </p>
                  </div>
                </div>
                <div className="max-w-xl space-y-2">
                  <label className="sr-only" htmlFor="heysummit-api-token">
                    HeySummit API Token
                  </label>
                  <div className="relative">
                    <Input
                      id="heysummit-api-token"
                      type={heysummitReveal ? "text" : "password"}
                      autoComplete="off"
                      placeholder="API Token"
                      aria-label="HeySummit API Token"
                      value={heysummitToken}
                      onChange={(e) => setHeysummitToken(e.target.value)}
                      disabled={heysummitBusy}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                      onClick={() => setHeysummitReveal((v) => !v)}
                      aria-label={heysummitReveal ? "Hide Token" : "Show Token"}
                    >
                      {heysummitReveal ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                  {heysummitError ? (
                    <p role="alert" className="text-sm text-rs-danger">
                      {heysummitError}
                    </p>
                  ) : null}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={heysummitBusy}
                    onClick={() => {
                      setHeysummitConnecting(false);
                      setHeysummitToken("");
                      setHeysummitError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={heysummitBusy || !heysummitToken.trim()}
                    onClick={() => void saveHeySummitToken()}
                  >
                    {heysummitBusy ? "Connecting…" : "Connect HeySummit"}
                  </Button>
                </div>
              </div>
            );
          }

          if (source.provider === "fluent_forms" && fluentFormsConnecting) {
            return (
              <div
                key={source.provider}
                ref={sourceConnectPanelRef}
                className="space-y-4 rounded-rs border border-rs-border bg-rs-bg p-4"
              >
                <div className="flex items-start gap-3">
                  <ProviderLogo provider="fluent_forms" label="Fluent Forms" />
                  <div>
                    <h3 className="text-base font-semibold text-rs-ink">
                      Connect Fluent Forms
                    </h3>
                    <p className="mt-1 text-sm text-rs-ink-muted">
                      Enter your WordPress site URL and an Application Password
                      for a user with Fluent Forms permissions. After
                      connecting, add a Pro webhook feed with the URL and secret
                      AttendeeSync shows for that connection.
                    </p>
                  </div>
                </div>
                <div className="max-w-xl space-y-3">
                  <div>
                    <label className="sr-only" htmlFor="fluent-forms-site-url">
                      WordPress Site URL
                    </label>
                    <Input
                      id="fluent-forms-site-url"
                      type="url"
                      autoComplete="url"
                      placeholder="https://example.com"
                      aria-label="WordPress Site URL"
                      value={fluentFormsSiteUrl}
                      onChange={(e) => setFluentFormsSiteUrl(e.target.value)}
                      disabled={fluentFormsBusy}
                    />
                  </div>
                  <div>
                    <label className="sr-only" htmlFor="fluent-forms-username">
                      WordPress Username
                    </label>
                    <Input
                      id="fluent-forms-username"
                      type="text"
                      autoComplete="username"
                      placeholder="Username"
                      aria-label="WordPress Username"
                      value={fluentFormsUsername}
                      onChange={(e) => setFluentFormsUsername(e.target.value)}
                      disabled={fluentFormsBusy}
                    />
                  </div>
                  <div className="relative">
                    <label
                      className="sr-only"
                      htmlFor="fluent-forms-app-password"
                    >
                      Application Password
                    </label>
                    <Input
                      id="fluent-forms-app-password"
                      type={fluentFormsReveal ? "text" : "password"}
                      autoComplete="off"
                      placeholder="Application Password"
                      aria-label="Application Password"
                      value={fluentFormsPassword}
                      onChange={(e) => setFluentFormsPassword(e.target.value)}
                      disabled={fluentFormsBusy}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                      onClick={() => setFluentFormsReveal((v) => !v)}
                      aria-label={
                        fluentFormsReveal ? "Hide Password" : "Show Password"
                      }
                    >
                      {fluentFormsReveal ? (
                        <EyeOff className="size-4" />
                      ) : (
                        <Eye className="size-4" />
                      )}
                    </button>
                  </div>
                  {fluentFormsError ? (
                    <p role="alert" className="text-sm text-rs-danger">
                      {fluentFormsError}
                    </p>
                  ) : null}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={fluentFormsBusy}
                    onClick={() => {
                      setFluentFormsConnecting(false);
                      setFluentFormsSiteUrl("");
                      setFluentFormsUsername("");
                      setFluentFormsPassword("");
                      setFluentFormsError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={
                      fluentFormsBusy ||
                      !fluentFormsSiteUrl.trim() ||
                      !fluentFormsUsername.trim() ||
                      !fluentFormsPassword.trim()
                    }
                    onClick={() => void saveFluentFormsCredentials()}
                  >
                    {fluentFormsBusy ? "Connecting…" : "Connect Fluent Forms"}
                  </Button>
                </div>
              </div>
            );
          }

          if (source.provider === "mailchimp" && mailchimpConnecting) {
            return (
              <div
                key={source.provider}
                ref={sourceConnectPanelRef}
                className="space-y-4 rounded-rs border border-rs-border bg-rs-bg p-4"
              >
                <div className="flex items-start gap-3">
                  <ProviderLogo provider="mailchimp" label="Mailchimp" />
                  <div>
                    <h3 className="text-base font-semibold text-rs-ink">
                      Connect Mailchimp
                    </h3>
                    <p className="mt-1 text-sm text-rs-ink-muted">
                      {mailchimpConnecting === "choose"
                        ? "Authorize with Mailchimp OAuth, or paste an API key from Account → Extras → API keys."
                        : "Paste an API key that ends with your datacenter suffix (for example …-us19)."}
                    </p>
                  </div>
                </div>
                {mailchimpConnecting === "choose" ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={mailchimpBusy}
                      onClick={() => {
                        setMailchimpConnecting(null);
                        setMailchimpError(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={mailchimpBusy}
                      onClick={() => {
                        setMailchimpConnecting("api_key");
                        setMailchimpError(null);
                      }}
                    >
                      Use API Key
                    </Button>
                    {mailchimpOAuth !== false ? (
                      <Button
                        type="button"
                        disabled={mailchimpBusy}
                        onClick={() => void startMailchimpOAuth()}
                      >
                        {mailchimpBusy
                          ? "Connecting…"
                          : "Connect with Mailchimp"}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className="max-w-xl space-y-2">
                      <label className="sr-only" htmlFor="mailchimp-api-key">
                        Mailchimp API Key
                      </label>
                      <div className="relative">
                        <Input
                          id="mailchimp-api-key"
                          type={mailchimpReveal ? "text" : "password"}
                          autoComplete="off"
                          placeholder="API Key"
                          aria-label="Mailchimp API Key"
                          value={mailchimpApiKey}
                          onChange={(e) => setMailchimpApiKey(e.target.value)}
                          disabled={mailchimpBusy}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                          onClick={() => setMailchimpReveal((v) => !v)}
                          aria-label={mailchimpReveal ? "Hide Key" : "Show Key"}
                        >
                          {mailchimpReveal ? (
                            <EyeOff className="size-4" />
                          ) : (
                            <Eye className="size-4" />
                          )}
                        </button>
                      </div>
                      {mailchimpError ? (
                        <p role="alert" className="text-sm text-rs-danger">
                          {mailchimpError}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={mailchimpBusy}
                        onClick={() => {
                          setMailchimpConnecting("choose");
                          setMailchimpApiKey("");
                          setMailchimpError(null);
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        disabled={mailchimpBusy || !mailchimpApiKey.trim()}
                        onClick={() => void saveMailchimpApiKey()}
                      >
                        {mailchimpBusy ? "Connecting…" : "Connect Mailchimp"}
                      </Button>
                    </div>
                  </>
                )}
                {mailchimpConnecting === "choose" && mailchimpError ? (
                  <p role="alert" className="text-sm text-rs-danger">
                    {mailchimpError}
                  </p>
                ) : null}
                {mailchimpConnecting === "api_key" ? (
                  <p className="text-sm text-rs-ink-muted">
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => setMailchimpHelpOpen(true)}
                    >
                      How do I get an API key?
                    </Button>
                  </p>
                ) : null}
              </div>
            );
          }

          if (source.provider === "eventbrite" && eventbriteConnecting) {
            return (
              <div
                key={source.provider}
                ref={sourceConnectPanelRef}
                className="space-y-4 rounded-rs border border-rs-border bg-rs-bg p-4"
              >
                <div className="flex items-start gap-3">
                  <ProviderLogo provider="eventbrite" label="Eventbrite" />
                  <div>
                    <h3 className="text-base font-semibold text-rs-ink">
                      Connect Eventbrite
                    </h3>
                    <p className="mt-1 text-sm text-rs-ink-muted">
                      {eventbriteConnecting === "choose"
                        ? "Authorize with OAuth or paste a private token from Eventbrite Account Settings → Developer Links."
                        : "Paste your Eventbrite private token."}
                    </p>
                  </div>
                </div>
                {eventbriteConnecting === "choose" ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={eventbriteBusy}
                      onClick={() => {
                        setEventbriteConnecting(null);
                        setEventbriteError(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={eventbriteBusy}
                      onClick={() => {
                        setEventbriteConnecting("private_token");
                        setEventbriteError(null);
                      }}
                    >
                      Use Private Token
                    </Button>
                    {eventbriteOAuth !== false ? (
                      <Button
                        type="button"
                        disabled={eventbriteBusy}
                        onClick={() => void startEventbriteOAuth()}
                      >
                        {eventbriteBusy
                          ? "Connecting…"
                          : "Connect with Eventbrite"}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <div className="max-w-xl space-y-2">
                      <label
                        className="sr-only"
                        htmlFor="eventbrite-private-token"
                      >
                        Eventbrite Private Token
                      </label>
                      <div className="relative">
                        <Input
                          id="eventbrite-private-token"
                          type={eventbriteReveal ? "text" : "password"}
                          autoComplete="off"
                          placeholder="Private Token"
                          aria-label="Eventbrite Private Token"
                          value={eventbriteToken}
                          onChange={(e) => setEventbriteToken(e.target.value)}
                          disabled={eventbriteBusy}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-ink-muted"
                          onClick={() => setEventbriteReveal((v) => !v)}
                          aria-label={
                            eventbriteReveal ? "Hide Token" : "Show Token"
                          }
                        >
                          {eventbriteReveal ? (
                            <EyeOff className="size-4" />
                          ) : (
                            <Eye className="size-4" />
                          )}
                        </button>
                      </div>
                      {eventbriteError ? (
                        <p role="alert" className="text-sm text-rs-danger">
                          {eventbriteError}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={eventbriteBusy}
                        onClick={() => {
                          setEventbriteConnecting("choose");
                          setEventbriteToken("");
                          setEventbriteError(null);
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        disabled={eventbriteBusy || !eventbriteToken.trim()}
                        onClick={() => void saveEventbritePrivateToken()}
                      >
                        {eventbriteBusy ? "Connecting…" : "Connect Eventbrite"}
                      </Button>
                    </div>
                  </>
                )}
                {eventbriteConnecting === "choose" && eventbriteError ? (
                  <p role="alert" className="text-sm text-rs-danger">
                    {eventbriteError}
                  </p>
                ) : null}
                {eventbriteConnecting === "private_token" ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-rs-ink-muted">
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => setEventbriteHelpOpen(true)}
                    >
                      How do I get an API key?
                    </Button>
                    <span aria-hidden="true">·</span>
                    <a
                      href={EVENTBRITE_FORM_FILL_BOOKMARKLET}
                      title="Drag this link to your bookmarks bar, then click it while on Eventbrite's Request a new key page"
                      className="underline underline-offset-2"
                      onClick={(e) => e.preventDefault()}
                    >
                      Fill the form for me (drag to bookmarks bar)
                    </a>
                  </div>
                ) : null}
              </div>
            );
          }

          return (
            <div
              key={source.provider}
              className="flex w-full flex-col gap-4 rounded-rs border border-rs-border bg-rs-bg p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-start gap-3">
                <ProviderLogo provider={source.provider} label={source.label} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-rs-ink">{source.label}</p>
                    {connected ? (
                      <StatusBadge tone="ready">Connected</StatusBadge>
                    ) : (
                      <StatusBadge tone="needs_configuration">
                        Not Connected
                      </StatusBadge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-rs-ink-muted">
                    {connected && detail ? detail : source.description}
                  </p>
                  {connected &&
                  source.provider === "fluent_forms" &&
                  active?.webhookUrl &&
                  active.webhookSecret ? (
                    <div className="mt-3 space-y-2 rounded-rs-sm border border-rs-border bg-rs-elevated p-3 text-sm">
                      <p className="font-medium text-rs-ink">
                        Pro Webhook Setup
                      </p>
                      <p className="text-rs-ink-muted">
                        In Fluent Forms Pro → form Settings → WebHook, add a
                        feed with Request Format JSON, Body All Fields, and
                        header{" "}
                        <code className="text-xs">Authorization: Bearer …</code>{" "}
                        using the secret below.
                      </p>
                      <div>
                        <p className="text-xs font-medium text-rs-ink-muted">
                          Webhook URL
                        </p>
                        <p className="break-all font-mono text-xs text-rs-ink">
                          {active.webhookUrl}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-rs-ink-muted">
                          Bearer Secret
                        </p>
                        <p className="break-all font-mono text-xs text-rs-ink">
                          {active.webhookSecret}
                        </p>
                      </div>
                    </div>
                  ) : null}
                  {connected && source.provider === "linkedin_events" ? (
                    <div className="mt-3 space-y-3 rounded-rs-sm border border-rs-border bg-rs-elevated p-3 text-sm">
                      <p className="font-medium text-rs-ink">
                        Lead Notifications
                      </p>
                      <p className="text-rs-ink-muted">
                        Enable LinkedIn Lead Sync for specific events.
                        Subscriptions are created per Company Page event.
                        Requires platform Lead Sync approval.
                      </p>
                      {(() => {
                        const linkedinActive = activeConnection(
                          sourceConnections,
                          "linkedin_events",
                        );
                        if (!linkedinActive) return null;
                        const meta = linkedinActive.metaJson
                          ? JSON.parse(linkedinActive.metaJson)
                          : { organizationUrns: [] };
                        if (!meta.organizationUrns?.length) {
                          return (
                            <p className="text-xs text-rs-ink-muted">
                              No Company Pages found. Reconnect to refresh.
                            </p>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            {meta.organizationUrns.map((orgUrn: string) => (
                              <div
                                key={orgUrn}
                                className="rounded-rs-sm border border-rs-border bg-rs-bg p-2"
                              >
                                <p className="text-xs font-medium text-rs-ink">
                                  {orgUrn}
                                </p>
                                <p className="text-xs text-rs-ink-muted">
                                  Use the Create Stream wizard to enable Lead
                                  Sync for specific events on this Company Page.
                                </p>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 justify-end gap-2 sm:pl-4">
                {canManage ? (
                  <>
                    {connected && active ? (
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={Boolean(sourceDisconnectBusy)}
                        onClick={() =>
                          void disconnectSource(
                            source.provider,
                            source.label,
                            active.id,
                          )
                        }
                      >
                        {sourceDisconnectBusy === disconnectBusyId
                          ? "Disconnecting…"
                          : "Disconnect"}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant={connected ? "secondary" : "primary"}
                      disabled={
                        Boolean(sourceConnectBusy) || credentialSourceConnecting
                      }
                      onClick={() =>
                        void connectSource(source.provider, source.label)
                      }
                    >
                      {busy
                        ? "Connecting…"
                        : connected
                          ? "Reconnect"
                          : "Connect"}
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <ConfirmDialog
        open={mailchimpHelpOpen}
        onOpenChange={setMailchimpHelpOpen}
        title="How do I get a Mailchimp API key?"
        description="Create a key in your Mailchimp account, then paste it here."
        impact={
          <ol className="list-decimal space-y-1 pl-4 text-sm text-rs-ink">
            <li>Log into Mailchimp.</li>
            <li>Click Accounts &amp; Billing.</li>
            <li>Click Extras, then API Keys.</li>
            <li>Click Create a Key.</li>
            <li>Give the key an identifiable name (like AttendeeSync).</li>
            <li>Click Generate Key.</li>
            <li>Copy that key and paste it into AttendeeSync.</li>
          </ol>
        }
        confirmLabel="Got it"
        cancelLabel="Close"
        onConfirm={() => setMailchimpHelpOpen(false)}
      />
      <ConfirmDialog
        open={eventbriteHelpOpen}
        onOpenChange={setEventbriteHelpOpen}
        title="How do I get an Eventbrite API key?"
        description="Request a key in your Eventbrite account, then paste the private token here."
        impact={
          <div className="space-y-2 text-sm text-rs-ink">
            <p className="font-medium">Getting to the form:</p>
            <ol className="list-decimal space-y-1 pl-4">
              <li>Click Account Settings in the top-right account menu.</li>
              <li>At the bottom click Developer Links, then API Keys.</li>
              <li>Fill out the Request a new key form.</li>
            </ol>
            <p className="font-medium">Field values:</p>
            <ul className="list-disc space-y-1 pl-4">
              <li>
                Application URL:{" "}
                <code className="text-xs">https://app.attendeesync.com</code>
              </li>
              <li>
                OAuth Redirect URI:{" "}
                <code className="text-xs">
                  https://app.attendeesync.com/oauth/eventbrite/callback
                </code>
              </li>
              <li>Application Name: AttendeeSync</li>
              <li>
                Description: brief (e.g. &ldquo;Syncs event registrants into my
                marketing stack&rdquo;)
              </li>
              <li>Check the terms box, then click Create Key.</li>
            </ul>
            <p className="font-medium text-rs-danger">
              Important: after creating the key, expand it and copy the PRIVATE
              TOKEN (not the API key, client secret, or public token) — that is
              the value to paste into AttendeeSync.
            </p>
          </div>
        }
        contentClassName="w-[min(37rem,calc(100vw-2rem))]"
        confirmLabel="Got it"
        cancelLabel="Close"
        onConfirm={() => setEventbriteHelpOpen(false)}
      />
    </Panel>
  );
}
